// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl, type IntlShape } from 'react-intl';
import { AutoSizer, Column, SortDirection } from 'react-virtualized';

import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import {
  addOrUpdateUserCatalogInfo,
  removeUsersFromCatalog,
} from 'state/commands/catalogCommands';
import type {
  Catalog,
  Dispatch,
  Role,
  User,
  UserCatalogInfo,
} from 'state/types';
import {
  getIdentitySession,
  getRoles,
  getUserCatalogInfo,
  getUsername,
  getUsers,
} from 'state/selectors';
import { actions as catalogActions } from 'state/reducers/catalogReducer';
import { getUsers as getUsersAction } from 'state/commands/userCommands';
import { getRoles as getRolesAction } from 'state/commands/roleCommands';
import { renderUserType, sortUsers } from 'util/userUtils';
import {
  getNewSelectionState,
  getSelectedIndices,
  isItemAtIndexSelected,
  isItemWithIdSelected,
  reindexSelectedItems,
  type SelectionState,
  type SortDirectionType,
} from 'util/tableUtils';
import UserRolePicker from './UserRolePicker';
import {
  CATALOG_STATUS,
  CONFIRM_USER_ROLE_CHANGE_PANEL_ID,
} from 'util/constants';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import ConfirmUserRoleChangeDialog, {
  type ConfirmRoleChangePanelData,
} from './ConfirmUserRoleChangeDialog';
import { doesUserMatchUserName, isAdminUser } from 'util/userUtils';
import MultipleSelectionTable from 'view/App/MultipleSelectionTable';

const { getUserCatalogInfo: getUserCatalogInfoAction } = catalogActions;

type Props = {
  addOrUpdateUserCatalogInfo: typeof addOrUpdateUserCatalogInfo,
  catalog: Catalog,
  getRolesAction: typeof getRolesAction,
  getUserCatalogInfoAction: typeof getUserCatalogInfoAction,
  getUsersAction: typeof getUsersAction,
  identitySession: string,
  intl: IntlShape,
  loggedInUserName: string,
  removeUsersFromCatalog: typeof removeUsersFromCatalog,
  roles: Role[],
  showDialogPanel: typeof showDialogPanel,
  userCatalogInfo: UserCatalogInfo[],
  users: User[],
};

type State = {
  selectionState: SelectionState,
  sortBy: string,
  sortDirection: SortDirectionType,
  sortedUsers: User[],
};

const iconWidth = 35;
const connectedWidth = 120;
const roleWidth = 180;

class CatalogUsersPage extends Component<Props, State> {
  confirmDialogCallback: Function;
  handleChangeRole: Function;
  handleSelectionChanged: Function;

  sort: Function;
  userConnectedCellRenderer: Function;
  userRoleCellRenderer: Function;
  userTypeCellRenderer: Function;

  state = {
    selectionState: getNewSelectionState(),
    sortDirection: SortDirection.ASC,
    sortBy: 'name',
    sortedUsers: [],
  };

  constructor() {
    super();
    this.confirmDialogCallback = this.confirmDialogCallback.bind(this);
    this.handleChangeRole = this.handleChangeRole.bind(this);
    this.handleSelectionChanged = this.handleSelectionChanged.bind(this);
    this.sort = this.sort.bind(this);
    this.userConnectedCellRenderer = this.userConnectedCellRenderer.bind(this);
    this.userRoleCellRenderer = this.userRoleCellRenderer.bind(this);
    this.userTypeCellRenderer = this.userTypeCellRenderer.bind(this);
  }

  componentDidMount() {
    const { catalog, identitySession } = this.props;
    this.props.getUserCatalogInfoAction(identitySession, catalog.name);
    this.props.getUsersAction();
    this.props.getRolesAction();
  }

  mergeUserCatInfo(users: User[], userCatalogInfo: UserCatalogInfo[]) {
    return users.map(user => {
      const userCatInfo = userCatalogInfo.find(
        info => info.accountName === user.name
      );
      if (userCatInfo) {
        return {
          ...user,
          roleName: userCatInfo.roleName,
          userStatus: userCatInfo.userStatus,
        };
      } else
        return {
          ...user,
          roleName: '',
        };
    });
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.userCatalogInfo !== nextProps.userCatalogInfo) {
      // Merge membership info into user list
      const sortedUsers = sortUsers(
        this.mergeUserCatInfo(nextProps.users, nextProps.userCatalogInfo),
        this.state.sortBy,
        this.state.sortDirection
      );
      this.setState({
        sortedUsers,
      });
    }
  }

  handleSelectionChanged(selectionState: SelectionState) {
    this.setState({ selectionState });
  }

  userTypeCellRenderer({
    cellData,
    columnData,
    columnIndex,
    dataKey,
    isScrolling,
    rowData,
    rowIndex,
  }) {
    return renderUserType(rowData, rowIndex, this.state.selectionState);
  }

  userConnectedCellRenderer({
    cellData,
    columnData,
    columnIndex,
    dataKey,
    isScrolling,
    rowData,
    rowIndex,
    style,
  }) {
    if (rowData.userStatus === CATALOG_STATUS.ONLINE) {
      const selectionContribution = isItemAtIndexSelected(
        rowIndex,
        this.state.selectionState
      )
        ? '-selected'
        : ' has-text-primary';
      return (
        <i
          className={`fa fa-check pseudo-table-icon${selectionContribution}`}
          style={style}
        />
      );
    }
  }

  handleChangeRole(user: User, roleName: string) {
    const { loggedInUserName } = this.props;
    const { selectionState, sortedUsers } = this.state;

    // filter out the admin user or logged in user from the affectedUsersCount
    const selectedUsersCount = getSelectedIndices(selectionState)
      .map(idx => sortedUsers[idx])
      .filter(
        u => !isAdminUser(u) && !doesUserMatchUserName(u, loggedInUserName)
      ).length;

    this.props.showDialogPanel(CONFIRM_USER_ROLE_CHANGE_PANEL_ID, {
      affectedUsersCount:
        (isItemWithIdSelected(user.uuid, selectionState) &&
          selectedUsersCount) ||
        1,
      user,
      roleName,
    });
  }

  userRoleCellRenderer({
    cellData,
    columnData,
    columnIndex,
    dataKey,
    isScrolling,
    rowData,
    rowIndex,
    style,
  }) {
    const { loggedInUserName, roles } = this.props;
    return (
      <UserRolePicker
        loggedInUserName={loggedInUserName}
        onChangeRole={this.handleChangeRole}
        roles={roles}
        user={rowData}
      />
    );
  }

  sort({ sortBy, sortDirection }) {
    const sortedUsers = sortUsers(
      this.mergeUserCatInfo(this.props.users, this.props.userCatalogInfo),
      sortBy,
      sortDirection
    );
    this.setState({
      selectionState: reindexSelectedItems(
        sortedUsers,
        this.state.selectionState
      ),
      sortBy,
      sortDirection,
      sortedUsers,
    });
  }

  confirmDialogCallback(data: ConfirmRoleChangePanelData) {
    const { user, roleName } = data;
    const { catalog, loggedInUserName } = this.props;
    const { selectionState, sortedUsers } = this.state;

    if (roleName) {
      let userInfo;
      if (isItemWithIdSelected(user.uuid, selectionState)) {
        userInfo = getSelectedIndices(selectionState)
          .map(idx => sortedUsers[idx])
          .filter(
            u => !isAdminUser(u) && !doesUserMatchUserName(u, loggedInUserName)
          )
          .map(u => ({
            accountName: u.name,
            roleName,
            catalogName: catalog.name,
          }));
      } else {
        userInfo = [
          {
            accountName: user.name,
            roleName,
            catalogName: catalog.name,
          },
        ];
      }

      this.props.addOrUpdateUserCatalogInfo(userInfo, catalog.name);
    } else {
      let usernames;
      // If the user is in the current selection, change all users in the selection.
      if (isItemWithIdSelected(user.uuid, selectionState)) {
        usernames = getSelectedIndices(selectionState)
          .map(idx => sortedUsers[idx])
          .filter(
            u => !isAdminUser(u) && !doesUserMatchUserName(u, loggedInUserName)
          )
          .map(u => u.name);
      } else {
        usernames = [user.name];
      }

      this.props.removeUsersFromCatalog(catalog.name, usernames);
    }
  }

  render() {
    const {
      intl: { formatMessage },
    } = this.props;
    const { selectionState, sortBy, sortDirection, sortedUsers } = this.state;

    return (
      <div className="page" data-test-name="catalog-user-page">
        <ConfirmUserRoleChangeDialog callback={this.confirmDialogCallback} />
        <AutoSizer>
          {({ height, width }) => {
            const leftoverWidth =
              width - (iconWidth + connectedWidth + roleWidth);
            return (
              <MultipleSelectionTable
                headerHeight={40}
                height={height}
                onSelectionChanged={this.handleSelectionChanged}
                rowHeight={30}
                selectionState={selectionState}
                sort={this.sort}
                sortBy={sortBy}
                sortDirection={sortDirection}
                sortedItems={sortedUsers}
                width={width}
              >
                <Column
                  cellRenderer={this.userTypeCellRenderer}
                  dataKey="fromLDAP"
                  width={iconWidth}
                />
                <Column
                  className="list-item"
                  dataKey="name"
                  label={formatMessage(localizedStrings.USER_ACCOUNT)}
                  width={leftoverWidth / 2}
                />
                <Column
                  className="list-item"
                  dataKey="longName"
                  label={formatMessage(localizedStrings.FULL_NAME)}
                  width={leftoverWidth / 2}
                />
                <Column
                  cellRenderer={this.userRoleCellRenderer}
                  className="list-item"
                  dataKey="roleName"
                  label={formatMessage(localizedStrings.USER_ROLE)}
                  width={roleWidth}
                />
                <Column
                  cellRenderer={this.userConnectedCellRenderer}
                  dataKey="userStatus"
                  label={formatMessage(localizedStrings.CATALOG_IS_CONNECTED)}
                  width={connectedWidth}
                />
              </MultipleSelectionTable>
            );
          }}
        </AutoSizer>
      </div>
    );
  }
}

export default connect(
  state => ({
    identitySession: getIdentitySession(state),
    loggedInUserName: getUsername(state),
    roles: getRoles(state),
    userCatalogInfo: getUserCatalogInfo(state),
    users: getUsers(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      addOrUpdateUserCatalogInfo,
      getRolesAction,
      getUserCatalogInfoAction,
      getUsersAction,
      removeUsersFromCatalog,
      showDialogPanel,
    })
)(injectIntl(CatalogUsersPage));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CatalogUsersPage.js